import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ToggleButton, Tooltip } from '@mui/material';
import { AttendanceRegistrationTypeDto, AttendanceStateDto } from '../../../../../api-service/clients';
import { getButtonColor } from '../utils';
import { getAttendanceStateColor } from '../../../attendance-state-colors';
import { attendanceButtonColumnMinWidth, attendanceIconFontSize, headerStateLabelStyle, registeredStateLabelOverlayStyle } from './attendance-column-layout';
import { getAttendanceStateDisplayName } from '../../../attendance-state-texts';
import { getAttendanceStateIcon } from '../../../attendance-state-icons';

interface ISelectableButtonProps {
  attendanceState: AttendanceStateDto;
  registrationType: AttendanceRegistrationTypeDto;
  selectedAttendanceState?: AttendanceStateDto;
  onClick: (attendanceState?: AttendanceStateDto) => void;
  isHeader?: boolean;
  disabled?: boolean;
  locked?: boolean;
  readOnly?: boolean;
}

export function SelectableButton(props: ISelectableButtonProps) {
  const { selectedAttendanceState, attendanceState, registrationType, onClick, disabled, isHeader = false, locked = false, readOnly = false } = props;

  const isSelected = selectedAttendanceState === attendanceState;

  function handleChange() {
    const attendanceType = isSelected ? undefined : attendanceState;
    onClick(attendanceType);
  }

  const backgroundColor = locked ? 'inherit' : getButtonColor(attendanceState, selectedAttendanceState);

  const iconColor = (() => {
    if (locked) {
      return getButtonColor(attendanceState, selectedAttendanceState);
    }
    if (isHeader && !disabled) {
      return getAttendanceStateColor(attendanceState);
    }
    return 'inherit';
  })();

  const showRegisteredStateLabel = isSelected;

  const tooltipTitle = isHeader && disabled && !locked ? 'Alle borgere er sat. Knapperne er væk' : '';

  const labelText = getAttendanceStateDisplayName(attendanceState, registrationType);
  // Udeblevet and Lovligt udeblevet read the same for both registrations, so the accessible name has to say which one it is.
  const accessibleName = `${labelText} (${registrationType === AttendanceRegistrationTypeDto.End ? 'Slut' : 'Start'})`;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" minWidth={attendanceButtonColumnMinWidth} position="relative">
      <Tooltip title={tooltipTitle}>
        <div>
          <ToggleButton
            value={attendanceState}
            selected={isSelected}
            aria-label={accessibleName}
            disabled={disabled || locked}
            disableRipple={readOnly}
            sx={{
              cursor: readOnly ? 'default' : undefined,
              width: 34,
              height: 34,
              padding: 0,
              border: 'none',
              borderRadius: '50%',
              backgroundColor,
              '&:hover': {
                backgroundColor,
              },
              '&.Mui-selected': {
                color: locked ? undefined : 'white',
                backgroundColor,
                '&:hover': {
                  color: locked ? undefined : 'white',
                  backgroundColor,
                },
              },
              '&.Mui-disabled': {
                border: 'none',
                backgroundColor,
                color: locked ? undefined : 'gray',
              },
            }}
            onChange={readOnly ? undefined : handleChange}
          >
            <FontAwesomeIcon color={iconColor} icon={getAttendanceStateIcon(attendanceState)} fontSize={attendanceIconFontSize} />
          </ToggleButton>
        </div>
      </Tooltip>
      {isHeader && <Box sx={headerStateLabelStyle}>{labelText}</Box>}
      {!isHeader && showRegisteredStateLabel && <Box sx={registeredStateLabelOverlayStyle}>{labelText}</Box>}
    </Box>
  );
}
